import { useState } from 'react';
import { Link, Head } from '@inertiajs/react';
import { AppShell } from '../../../components/layout';
import { Card } from '../../../components/ui';
import {
  FrequencyBadge,
  TierBadge,
  PosBadge,
  MorphemeBreakdown,
  WordFamilyTree,
  DefinitionList,
} from '../../../components/dictionary';
import { BookOpen, Check, CreativeCommons, Plus } from 'lucide-react';
import { cn } from '../../../lib/cn';

export default function StudentDictionaryShow({
  student,
  word,
  in_personal_list,
  card,
}) {
  const breadcrumb = (
    <>
      <Link
        href="/student/dictionary"
        className="hover:text-blue-600 dark:hover:text-blue-400"
      >
        Dictionary
      </Link>
      {'  ›  '}
      {word.lemma}
    </>
  );

  return (
    <AppShell student={student} scrollTitle={breadcrumb}>
      <Head title={`${word.lemma} - Dictionary`} />

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link
          href="/student/dictionary"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Dictionary
        </Link>

        <Card className="p-8 mb-8">
          <header className="mb-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  {word.lemma}
                </h1>
                {word.ipa && (
                  <p className="text-lg text-gray-500 dark:text-gray-400 font-mono">
                    /{word.ipa}/
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <FrequencyBadge
                  band={word.frequencyBand}
                  frequencyZipf={word.frequencyZipf}
                  size="lg"
                />
                <TierBadge tier={word.beckTier} size="lg" showTooltip />
                <PosBadge pos={word.pos} size="lg" />
              </div>
            </div>
            <div className="mt-4">
              <AddToMyWordsButton
                word={word}
                initiallyAdded={in_personal_list}
                card={card}
              />
            </div>
            {word.spellingVariantOf && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Spelling variant of{' '}
                <Link
                  href={`/student/dictionary/${word.spellingVariantOf.id}`}
                  className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline underline-offset-2"
                >
                  {word.spellingVariantOf.lemma}
                </Link>
              </p>
            )}
          </header>

          {card?.context_sentence && (
            <Section title="From your reading">
              <ContextBlock card={card} />
            </Section>
          )}

          <Section title="Definitions">
            <DefinitionList definitions={word.definitions} showSource={false} />
          </Section>

          {word.morphemes && word.morphemes.length > 0 && (
            <Section title="Morpheme Breakdown">
              <MorphemeBreakdown morphemes={word.morphemes} showMeaning />
            </Section>
          )}

          {word.family && (
            <Section title="Word Family">
              <WordFamilyTree family={word.family} />
            </Section>
          )}

          {(word.synonyms?.length > 0 || word.antonyms?.length > 0) && (
            <Section title="Related Words">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {word.synonyms?.length > 0 && (
                  <RelatedWordsSection
                    title="Synonyms"
                    words={word.synonyms}
                    colorClass="bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                  />
                )}
                {word.antonyms?.length > 0 && (
                  <RelatedWordsSection
                    title="Antonyms"
                    words={word.antonyms}
                    colorClass="bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                  />
                )}
              </div>
            </Section>
          )}
        </Card>

        <div className="flex flex-col items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-8">
          <p className="flex items-center gap-1.5">
            <CreativeCommons className="w-3.5 h-3.5 flex-shrink-0" />
            Dictionary data from{' '}
            <a
              href="https://en-word.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 dark:hover:text-gray-300"
            >
              Open English WordNet
            </a>
            {' '}(CC BY 4.0) and{' '}
            <a
              href="https://en.wiktionary.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600 dark:hover:text-gray-300"
            >
              Wiktionary
            </a>
            {' '}(CC BY-SA 3.0)
          </p>
        </div>
      </div>
    </AppShell>
  );
}

function ContextBlock({ card }) {
  const sourceLine = [card.book_title, card.chapter_title]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4">
      <p className="text-base text-gray-700 dark:text-gray-200 leading-relaxed">
        <HighlightedContext sentence={card.context_sentence} lemma={card.lemma} />
      </p>
      {sourceLine && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{sourceLine}</span>
        </p>
      )}
    </div>
  );
}

// Mirrors the teacher course-vocabulary `HighlightedContext` (Teacher/Courses/
// Vocabulary/Show.jsx): bolds the lemma + any inflected form inside the
// sentence so the student's eye lands on the target word. The trailing y/e
// of the lemma is trimmed before escaping so that y→ied (petrify → petrified)
// and silent-e drops (make → making) still match.
function HighlightedContext({ sentence, lemma }) {
  if (!sentence) return '—';
  if (!lemma) return sentence;

  const root =
    /y$/i.test(lemma) || /e$/i.test(lemma) ? lemma.slice(0, -1) : lemma;
  const escaped = root.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`\\b${escaped}\\w*\\b`, 'gi');
  const result = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(sentence)) !== null) {
    if (match.index > lastIndex) {
      result.push(sentence.slice(lastIndex, match.index));
    }
    result.push(
      <strong key={match.index} className="font-semibold text-gray-900 dark:text-white">
        {match[0]}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < sentence.length) {
    result.push(sentence.slice(lastIndex));
  }
  return result.length > 0 ? result : sentence;
}

function Section({ title, children }) {
  return (
    <section className="mb-8 last:mb-0">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {title}
      </h2>
      {children}
    </section>
  );
}

function AddToMyWordsButton({ word, initiallyAdded, card }) {
  const [added, setAdded] = useState(initiallyAdded);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState(null);

  if (added) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40">
        <Check className="w-4 h-4" />
        In your words
        {card?.mastery_bucket && (
          <span className="ml-1 text-xs text-green-600/80 dark:text-green-400/80 font-normal">
            · {card.mastery_bucket.replace('_', ' ')}
          </span>
        )}
      </span>
    );
  }

  const handleAdd = async () => {
    setStatus('loading');
    setErrorMsg(null);
    try {
      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]'
      )?.content;
      const res = await fetch('/student/vocabulary/words', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({ surface_form: word.lemma }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAdded(true);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Could not add word');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error');
    }
  };

  if (status === 'error') {
    return (
      <button
        onClick={handleAdd}
        className="text-sm text-red-500 hover:text-red-600 px-3 py-1.5"
        title={errorMsg || 'Retry'}
      >
        Retry
      </button>
    );
  }

  return (
    <button
      onClick={handleAdd}
      disabled={status === 'loading'}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg',
        'border border-gray-200 dark:border-gray-700',
        'text-gray-700 dark:text-gray-300',
        'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
        status === 'loading' && 'opacity-50 cursor-not-allowed'
      )}
    >
      <Plus className="w-4 h-4" />
      {status === 'loading' ? 'Adding…' : 'Add to my words'}
    </button>
  );
}

function RelatedWordsSection({ title, words, colorClass }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {words.map((word) => (
          <Link
            key={word.id}
            href={`/student/dictionary/${word.id}`}
            className={cn(
              'px-3 py-1 text-sm rounded-full transition-colors',
              colorClass
            )}
          >
            {word.lemma}
          </Link>
        ))}
      </div>
    </div>
  );
}
